import { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import NavigationDropdown from "../../components/NavigationDropdown";
import NavigationPanel from "../../components/NavigationPanel";
import PortalPopup from "../../components/PortalPopup";
import DateFilter from "../../components/DateFilter";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import LawFirm from "../../components/LawFirm";
import Breadcrumb from "../../components/Breadcrumb";
import Footer from "../../components/Footer";
import { documentService } from "../../services/documentService";
import { Link2 } from "iconsax-reactjs";
import PoweredByAITag from "../../components/PoweredByAITag";
import CTAs from "../../components/CTAs";

const Defence_2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL;

  const caseData = location.state?.caseData;
  const defaultCaseData = {
    name: "Paras Mistry",
    status: "Pending",
    price: "2000",
    type: "Debt Collection",
    contactInfo: {
      address: "21 Turning St., Southampton",
      phone: "+441234567890",
      email: "pm@think-ai.io",
      company: "Azure Solutions Ltd.",
    },
    claimDetails: { amountOwed: "£2000" },
  };

  const currentCase = caseData || defaultCaseData;

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [successLikelihood, setSuccessLikelihood] = useState(74);
  const [aiSummary, setAiSummary] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isLawFirmOpen, setLawFirmOpen] = useState(false);
  const [isDateFilterOpen, setDateFilterOpen] = useState(false);
  const [selectedDateTimeRange, setSelectedDateTimeRange] = useState("");

  const calculateSuccessLikelihood = (caseData, documents) => {
    let score = 50;
    const docCount = documents?.documents?.length || 0;
    if (docCount > 5) score += 15;
    else if (docCount > 2) score += 10;
    else if (docCount > 0) score += 5;
    const debtAmount = parseFloat(
      caseData.claimDetails.amountOwed.replace(/[^0-9.]/g, ""),
    );
    if (debtAmount <= 1000) score += 12;
    else if (debtAmount <= 5000) score += 8;
    else score += 5;
    const daysOverdue = Math.abs(
      Math.floor((new Date() - new Date(caseData.dueDate)) / 86400000),
    );
    if (daysOverdue <= 7) score += 10;
    else if (daysOverdue <= 30) score += 8;
    else if (daysOverdue <= 90) score += 5;
    else score += 2;
    if (caseData.contactInfo?.company) score += 8;
    if (caseData.status === "Pending") score += 7;
    return Math.min(Math.max(Math.round(score), 0), 100);
  };

  const getAISummary = (likelihood, caseData, documents) => {
    const docCount = documents?.documents?.length || 0;
    const debtAmount = caseData.claimDetails.amountOwed;
    if (likelihood >= 80)
      return `High probability of success. With ${docCount} documents supporting the ${debtAmount} claim, this case has strong evidence. Focus on legal proceedings.`;
    if (likelihood >= 60)
      return `Good recovery potential. The ${debtAmount} debt is well-documented with ${docCount} files. Consider negotiation before legal action.`;
    if (likelihood >= 40)
      return `Moderate case strength. For the ${debtAmount} claim, additional evidence would improve chances. Explore settlement options.`;
    return `Challenging case. The ${debtAmount} debt requires more substantial evidence or alternative resolution methods.`;
  };

  const getSuccessRecommendation = (likelihood, caseData) => {
    const d = caseData.claimDetails.amountOwed;
    if (likelihood >= 80)
      return `Proceed with legal action for ${d} claim — strong evidence present`;
    if (likelihood >= 60)
      return `Attempt negotiation for ${d} debt, then legal action if needed`;
    if (likelihood >= 40)
      return `Gather more evidence for ${d} claim before proceeding`;
    return `Consider alternative dispute resolution for ${d} debt`;
  };

  useEffect(() => {
    if (currentCase && documents) {
      const likelihood = calculateSuccessLikelihood(currentCase, documents);
      setSuccessLikelihood(likelihood);
      setAiSummary(getAISummary(likelihood, currentCase, documents));
      setRecommendation(getSuccessRecommendation(likelihood, currentCase));
    }
  }, [currentCase, documents]);

  useEffect(() => {
    fetchDocuments();
  }, [currentCase.id, activeTab, searchQuery, currentPage]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentService.getDocuments(currentCase.caseId);
      setDocuments(response);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      setError("Failed to fetch documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredDocuments = () => {
    if (!documents?.documents || !Array.isArray(documents.documents)) return [];
    const allFiles = [];
    documents.documents.forEach((doc) => {
      if (doc.files && Array.isArray(doc.files)) {
        doc.files.forEach((file) =>
          allFiles.push({
            ...file,
            documentId: doc.documentId,
            createdAt: doc.createdAt,
          }),
        );
      }
    });
    let filtered = allFiles;
    if (activeTab !== "all") {
      filtered = allFiles.filter((file) => {
        const ext = file.fileName.split(".").pop()?.toLowerCase();
        if (activeTab === "PDF") return ext === "pdf";
        if (activeTab === "PNG")
          return ["png", "jpg", "jpeg", "gif"].includes(ext);
        if (activeTab === "DOCX") return ["doc", "docx"].includes(ext);
        return true;
      });
    }
    if (searchQuery)
      filtered = filtered.filter((f) =>
        f.fileName?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    return filtered;
  };

  const breadcrumb = [
    { label: "Defence and Case Handling", path: "/defence1", active: false },
    { label: currentCase.name, path: "", active: true },
  ];

  const closeNavigationDropdown = useCallback(
    () => setNavigationDropdownOpen(false),
    [],
  );
  const closeLogoutConfirmation = useCallback(
    () => setLogoutConfirmationOpen(false),
    [],
  );
  const openLawFirm = useCallback(() => setLawFirmOpen(true), []);
  const closeLawFirm = useCallback(() => setLawFirmOpen(false), []);
  const openDateFilter = useCallback(() => setDateFilterOpen(true), []);
  const closeDateFilter = useCallback(() => setDateFilterOpen(false), []);

  const likelihoodColor =
    successLikelihood >= 80
      ? "#059669"
      : successLikelihood >= 60
        ? "#2563EB"
        : successLikelihood >= 40
          ? "#D97706"
          : "#DC2626";

  const likelihoodBg =
    successLikelihood >= 80
      ? "#ECFDF5"
      : successLikelihood >= 60
        ? "#EFF6FF"
        : successLikelihood >= 40
          ? "#FFFBEB"
          : "#FEF2F2";

  const nextSteps = [
    { label: "Review payment plan proposal", done: false },
    { label: "Prepare counter evidence", done: true },
    { label: "File response to debtor", done: true },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Mono:wght@400;500&display=swap');

        html, body { background: rgb(241,245,249) !important; }
        .d2-root * { box-sizing: border-box; }
        .d2-root { font-family: 'DM Sans', sans-serif; background: rgb(241,245,249); min-height: 100vh; }

        .d2-card {
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          position: relative; overflow: hidden;
        }
        .d2-card .accent-bar {
          position: absolute; top: 0; left: 0; right: 0;
          height: 4px; border-radius: 16px 16px 0 0;
        }

        .d2-info-row {
          display: flex; align-items: flex-start; justify-content: space-between;
          padding: 9px 0; border-bottom: 1px solid #F1F5F9; font-size: 14px;
        }
        .d2-info-row:last-child { border-bottom: none; padding-bottom: 0; }
        .d2-info-row:first-child { padding-top: 0; }
        .d2-info-label { color: #64748B; font-weight: 500; }
        .d2-info-value { color: #0F172A; font-weight: 600; text-align: right; max-width: 55%; }

        .d2-section-title { font-size: 15px; font-weight: 700; color: #0F172A; margin: 0 0 16px; }

        .d2-tab-row { display: inline-flex; background: #F1F5F9; border-radius: 8px; padding: 3px; gap: 2px; }
        .d2-tab {
          padding: 6px 16px; border-radius: 6px; font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #64748B; cursor: pointer;
          border: none; background: transparent; transition: background 0.15s, color 0.15s;
        }
        .d2-tab:hover { background: #ffffff; color: #0F172A; }
        .d2-tab.d2-active { background: #ffffff; color: #2563EB; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

        .d2-search {
          display: flex; align-items: center; gap: 8px; background: #ffffff;
          border: 1px solid #E2E8F0; border-radius: 10px; padding: 8px 12px;
          width: 220px; transition: border-color 0.15s;
        }
        .d2-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .d2-search input { border: none; outline: none; background: transparent; font-family: 'DM Sans', sans-serif; font-size: 13px; color: #0F172A; width: 100%; }
        .d2-search input::placeholder { color: #94A3B8; }

        .d2-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;
        }

        .d2-file-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 10px; background: #F8FAFC; border: 1px solid #E2E8F0;
          border-radius: 8px; font-size: 13px; font-weight: 600; color: #2563EB;
          cursor: pointer; text-decoration: none; transition: background 0.15s, border-color 0.15s;
        }
        .d2-file-chip:hover { background: #EFF6FF; border-color: #93C5FD; }

        .d2-step {
          display: flex; align-items: center; gap: 10px; padding: 8px 0;
          border-bottom: 1px solid #F1F5F9; font-size: 14px;
        }
        .d2-step:last-child { border-bottom: none; padding-bottom: 0; }

        .d2-law-btn {
          display: inline-flex; align-items: center; gap: 8px; padding: 9px 18px;
          background: #0F172A; color: #ffffff; border: none; border-radius: 10px;
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700;
          cursor: pointer; transition: background 0.15s;
        }
        .d2-law-btn:hover { background: #1E293B; }
      `}</style>

      <div
        className="d2-root w-full overflow-visible"
        style={{ background: "rgb(241,245,249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          <Breadcrumb breadcrumbs={breadcrumb} />

          {/* ── Page header ── */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#0F172A",
                }}
              >
                Defence and Case Handling
              </h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
                Acknowledge defences and revert back.
              </p>
            </div>
            <button className="d2-law-btn" onClick={openLawFirm}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm-1 14H9V8h2v8Zm4 0h-2V8h2v8Z"
                  fill="#ffffff"
                />
              </svg>
              Contact Law Firm
            </button>
          </div>

          {/* ── Top two info cards ── */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}
          >
            {/* Case Summary */}
            <div className="d2-card" style={{ padding: 24 }}>
              <div className="accent-bar" style={{ background: "#2563EB" }} />
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 18,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: "#EFF6FF",
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 22h6c5 0 7-2 7-7V9c0-5-2-7-7-7H9C4 2 2 4 2 9v6c0 5 2 7 7 7Z"
                        stroke="#2563EB"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M15.75 9h-7.5M15.75 15h-7.5"
                        stroke="#2563EB"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="d2-section-title" style={{ margin: 0 }}>
                    Case Summary
                  </p>
                </div>

                <div className="d2-info-row">
                  <span className="d2-info-label">Case ID</span>
                  <span
                    className="d2-info-value"
                    style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}
                  >
                    {currentCase.reference || "—"}
                  </span>
                </div>
                <div className="d2-info-row">
                  <span className="d2-info-label">Client name</span>
                  <span className="d2-info-value">{currentCase.name}</span>
                </div>
                <div className="d2-info-row">
                  <span className="d2-info-label">Debt amount</span>
                  <span
                    className="d2-info-value"
                    style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}
                  >
                    {currentCase.claimDetails.amountOwed}
                  </span>
                </div>
                <div className="d2-info-row">
                  <span className="d2-info-label">Due date</span>
                  <span className="d2-info-value">
                    {currentCase.dueDate
                      ? new Date(currentCase.dueDate).toLocaleDateString(
                          "en-GB",
                          { day: "numeric", month: "short", year: "numeric" },
                        )
                      : "—"}
                  </span>
                </div>
                <div className="d2-info-row">
                  <span className="d2-info-label">Status</span>
                  <span
                    className="d2-badge"
                    style={
                      currentCase.status === "Resolved"
                        ? { background: "#F0FDF4", color: "#166534" }
                        : { background: "#FEF2F2", color: "#991B1B" }
                    }
                  >
                    ● {currentCase.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Debtor Information */}
            <div className="d2-card" style={{ padding: 24 }}>
              <div className="accent-bar" style={{ background: "#7C3AED" }} />
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 18,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: "#F5F3FF",
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5Zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5Z"
                        fill="#7C3AED"
                      />
                    </svg>
                  </div>
                  <p className="d2-section-title" style={{ margin: 0 }}>
                    Debtor Information
                  </p>
                </div>

                <div className="d2-info-row">
                  <span className="d2-info-label">Full name</span>
                  <span className="d2-info-value">{currentCase.name}</span>
                </div>
                <div className="d2-info-row">
                  <span className="d2-info-label">Address</span>
                  <span className="d2-info-value">
                    {[
                      currentCase.contactInfo?.house,
                      currentCase.contactInfo?.street,
                      currentCase.contactInfo?.city,
                      currentCase.contactInfo?.country,
                      currentCase.contactInfo?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </span>
                </div>
                <div className="d2-info-row">
                  <span className="d2-info-label">Email</span>
                  <span className="d2-info-value" style={{ color: "#2563EB" }}>
                    {currentCase.contactInfo?.email || "—"}
                  </span>
                </div>
                <div className="d2-info-row">
                  <span className="d2-info-label">Contact number</span>
                  <span className="d2-info-value" style={{ color: "#2563EB" }}>
                    {currentCase.contactInfo?.phone || "—"}
                  </span>
                </div>
                <div className="d2-info-row">
                  <span className="d2-info-label">Company</span>
                  <span className="d2-info-value">
                    {currentCase.contactInfo?.company || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Bottom two cards ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "380px 1fr",
              gap: 18,
              alignItems: "start",
            }}
          >
            {/* Success Likelihood */}
            <div className="d2-card" style={{ padding: 24 }}>
              <div
                className="accent-bar"
                style={{ background: likelihoodColor }}
              />
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: likelihoodBg,
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                        stroke={likelihoodColor}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="d2-section-title" style={{ margin: 0 }}>
                    Likelihood of success
                  </p>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#94A3B8",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Score
                    </span>
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 20,
                        fontWeight: 500,
                        color: likelihoodColor,
                      }}
                    >
                      {successLikelihood}%
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      background: "#F1F5F9",
                      borderRadius: 99,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${successLikelihood}%`,
                        background: likelihoodColor,
                        borderRadius: 99,
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                </div>

                {/* Recommendation */}
                <div
                  style={{
                    padding: "14px 16px",
                    background: "#F8FAFC",
                    borderRadius: 10,
                    border: "1px solid #E2E8F0",
                    marginBottom: 16,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 4px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Recommended next steps
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      color: "#0F172A",
                      lineHeight: 1.5,
                    }}
                  >
                    {recommendation || "Submit evidence within 5 days."}
                  </p>
                </div>

                {/* AI Summary */}
                <div
                  style={{
                    padding: "14px 16px",
                    background: "#F8FAFC",
                    borderRadius: 10,
                    border: "1px solid #E2E8F0",
                    marginBottom: 16,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 4px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    AI Summary
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      color: "#0F172A",
                      lineHeight: 1.5,
                    }}
                  >
                    {aiSummary || "Analysing case data…"}
                  </p>
                </div>

                <PoweredByAITag />
              </div>
            </div>

            {/* Defence Strategy */}
            <div className="d2-card" style={{ padding: 24 }}>
              <div className="accent-bar" style={{ background: "#059669" }} />
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: "#ECFDF5",
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2Z"
                        stroke="#059669"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="d2-section-title" style={{ margin: 0 }}>
                    Defence Strategy &amp; Arguments
                  </p>
                </div>

                {/* Supporting evidence */}
                <div
                  style={{
                    marginBottom: 24,
                    paddingBottom: 20,
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Supporting evidence
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {Array.isArray(documents?.documents) &&
                    documents.documents.length > 0 ? (
                      documents.documents.map((file, index) => (
                        <a
                          key={file.id || index}
                          className="d2-file-chip"
                          href={
                            file.fileUrl ||
                            `${API_URL}/api/official_documents/file/${currentCase.caseId}/${file.fileIndex}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                              stroke="#2563EB"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                              stroke="#2563EB"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {file.name || `Document ${index + 1}`}
                        </a>
                      ))
                    ) : (
                      <span
                        style={{
                          fontSize: 13,
                          color: "#94A3B8",
                          fontStyle: "italic",
                        }}
                      >
                        No supporting documents uploaded.
                      </span>
                    )}
                  </div>
                </div>

                {/* Next Steps */}
                <div>
                  <p
                    style={{
                      margin: "0 0 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Next steps
                  </p>
                  {nextSteps.map((step, i) => (
                    <div key={i} className="d2-step">
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: step.done ? "#ECFDF5" : "#EFF6FF",
                          border: `1.5px solid ${step.done ? "#059669" : "#2563EB"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {step.done ? (
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M5 13l4 4L19 7"
                              stroke="#059669"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "#2563EB",
                            }}
                          />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 14,
                          color: step.done ? "#94A3B8" : "#0F172A",
                          textDecoration: step.done ? "line-through" : "none",
                          fontWeight: step.done ? 400 : 600,
                        }}
                      >
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {isNavigationDropdownOpen && (
        <PortalPopup
          overlayColor="rgba(113,113,113,0.3)"
          placement="Centered"
          onOutsideClick={closeNavigationDropdown}
        >
          <NavigationDropdown onClose={closeNavigationDropdown} />
        </PortalPopup>
      )}
      {isDateFilterOpen && (
        <PortalPopup
          overlayColor="rgba(113,113,113,0.3)"
          placement="Centered"
          onOutsideClick={closeDateFilter}
        >
          <DateFilter
            onClose={closeDateFilter}
            onDateTimeSelect={(r) => setSelectedDateTimeRange(r)}
          />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113,113,113,0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isLawFirmOpen && (
        <PortalPopup
          overlayColor="rgba(113,113,113,0.3)"
          placement="Centered"
          onOutsideClick={closeLawFirm}
        >
          <LawFirm onClose={closeLawFirm} />
        </PortalPopup>
      )}
    </>
  );
};

export default Defence_2;
